import { JobApplicationStatus } from '../../../domain/types/jobApplicationStatus';
import { FiltersMapper } from '../../../hooks/use-filters/useFilters';
import { SortOrder } from '../../../providers/pagination';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import { serpJobApplicationAdvancedFiltersMapper } from '../../containers/filter-serp-job-applications-action/mappers';
import {
  CombinedJobApplicationFilters,
  JobApplicationSerpPageFilterKey,
  JobApplicationSerpPageFilterOption,
  JobApplicationSerpPageFilterOptionCtx,
  JobApplicationSerpPageFilters,
  JobApplicationSerpSortOption,
  SORT_KEYS,
} from './types';

export const LOAD_SERP_JOB_APPLICATIONS_ERROR_MESSAGE = 'Non è stato possibile recuperare le candidature.';
export const NO_SERP_JOB_APPLICATIONS_MESSAGE = 'Non sono presenti candidature.';

/**
 * "Da lavorare"
 *
 * - Agent: non previsto
 *
 * - HR/admin: candidature senza gestore HR (supervisor) selezionato.
 */
export const TO_BE_MANAGED_FILTER_OPTION: JobApplicationSerpPageFilterOption = {
  key: 'toBeManaged',
  label: {
    short: 'Da lavorare',
    long: 'Da lavorare',
  },
  value: {
    toBeManaged: true,
    status: JobApplicationStatus.IN_PROGRESS,
  },
};

/**
 * "In lavorazione":
 *
 * - Agent: candidature assegnate (status ASSIGNED) al proprio team
 *   (email aggiunte dinamicamente nella pagina).
 *
 * - HR/admin: candidature con gestore (supervisor) selezionato ma senza assegnatario.
 */
export const IN_MANAGEMENT_FILTER_OPTION = (ctx: JobApplicationSerpPageFilterOptionCtx): JobApplicationSerpPageFilterOption => ({
  key: 'inManagement',
  label: {
    short: 'In lavorazione',
    long: 'In lavorazione',
  },
  value: ctx.userIsAgent
    ? {
      status: JobApplicationStatus.ASSIGNED,
    }
    : {
      toBeAssigned: true,
      status: JobApplicationStatus.IN_PROGRESS,
    },
});

/**
 * "Tutte":
 *
 * - Agent: tutte le candidature con supervisor selezionato per cui un agente del proprio team è assegnatario o intervistatore
 *  (email aggiunte dinamicamente nella pagina).
 *
 * - HR/admin: tutte le candidature senza condizioni.
 */
export const ALL_JOB_APPLICATIONS_FILTER_OPTION: JobApplicationSerpPageFilterOption = {
  key: 'all',
  label: {
    short: 'Tutte',
    long: 'Tutte le candidature',
  },
  value: {},
};

/**
 * Gruppo "Stato" con opzioni specifiche:
 *
 * - Agent: gruppo non previsto.
 *
 * - HR/admin: sulla base dello stato selezionato.
 */
export const STATUS_FILTER_OPTIONS: JobApplicationSerpPageFilterOption[] = [
  {
    key: 'assigned',
    label: {
      short: 'Assegnate',
      long: 'Assegnate',
    },
    value: {
      status: [JobApplicationStatus.ASSIGNED, JobApplicationStatus.INTERVIEW_SCHEDULED],
    },
  },
  {
    key: 'inProposal',
    label: {
      short: 'In proposta',
      long: 'In proposta',
    },
    value: {
      status: JobApplicationStatus.OFFER,
    },
  },
  {
    key: 'archived',
    label: {
      short: 'Archiviate',
      long: 'Archiviate',
    },
    value: {
      status: [
        JobApplicationStatus.TALENT_POOL,
        JobApplicationStatus.KO_UNREACHABLE,
        JobApplicationStatus.KO_NOT_INTERESTED,
        JobApplicationStatus.KO_OFFER_REFUSED,
        JobApplicationStatus.KO_WRONG_NUMBER,
      ],
    },
  },
  {
    key: 'trashed',
    label: {
      short: 'Cestinate',
      long: 'Cestinate',
    },
    value: {
      status: [
        JobApplicationStatus.KO_NOT_ELIGIBLE,
        JobApplicationStatus.KO_DUPLICATE,
        JobApplicationStatus.KO_TEST,
      ],
    },
  },
];

/**
 * Gruppo "Colloquio" con opzioni specifiche:
 *
 * - candidature con colloquio passato o futuro (a seconda del valore selezionato):
 *
 * ed in più
 *
 *  - Agent: per cui un agente del proprio team è assegnatario o intervistatore
 *  (email aggiunte dinamicamente nella pagina).
 *
 * - HR/admin: senza condizioni.
 */
export const INTERVIEW_FILTER_OPTIONS: JobApplicationSerpPageFilterOption[] = [
  {
    key: 'pastInterviews',
    label: {
      short: 'Passati',
      long: 'Con colloquio passato',
    },
    value: {
      interviewDateLt: new Date().toISOString(),
      status: JobApplicationStatus.INTERVIEW_SCHEDULED,
    },
  },
  {
    key: 'futureInterviews',
    label: {
      short: 'Futuri',
      long: 'Con colloquio futuro',
    },
    value: {
      interviewDateGt: new Date().toISOString(),
      status: JobApplicationStatus.INTERVIEW_SCHEDULED,
    },
  },
];

export const DEFAULT_FILTER_OPTION = (ctx: JobApplicationSerpPageFilterOptionCtx): JobApplicationSerpPageFilterKey => (
  ctx.userIsAgent ? 'inManagement' : 'toBeManaged'
);

export const FILTER_OPTIONS = (ctx: JobApplicationSerpPageFilterOptionCtx): JobApplicationSerpPageFilterOption[] => removeNullAndUndefinedValues([
  ...(ctx.userIsHR || ctx.userIsAdmin ? [TO_BE_MANAGED_FILTER_OPTION] : []),
  IN_MANAGEMENT_FILTER_OPTION(ctx),
  ...((ctx.userIsHR || ctx.userIsAdmin) ? STATUS_FILTER_OPTIONS : []),
  ...INTERVIEW_FILTER_OPTIONS,
  ALL_JOB_APPLICATIONS_FILTER_OPTION,
]);

export const GROUPED_FILTER_OPTIONS = (ctx: JobApplicationSerpPageFilterOptionCtx) => removeNullAndUndefinedValues([
  (ctx.userIsHR || ctx.userIsAdmin) ? {
    label: 'Stato',
    options: STATUS_FILTER_OPTIONS,
  } : undefined,
  {
    label: 'Colloqui',
    options: INTERVIEW_FILTER_OPTIONS,
  },
]);

export const DEFAULT_SORT_OPTION: JobApplicationSerpSortOption = {
  key: 'default',
  label: 'In evidenza',
  value: {
    updatedAt: SortOrder.DESC,
  },
};

export const MOST_RECENT_CREATION_SORT_OPTION: JobApplicationSerpSortOption = {
  key: 'mostRecentCreation',
  label: 'Creazione più recente',
  value: {
    createdAt: SortOrder.DESC,
  },
};

export const LEAST_RECENT_CREATION_SORT_OPTION: JobApplicationSerpSortOption = {
  key: 'leastRecentCreation',
  label: 'Creazione meno recente',
  value: {
    createdAt: SortOrder.ASC,
  },
};

export const MOST_RECENT_UPDATE_SORT_OPTION: JobApplicationSerpSortOption = {
  key: 'mostRecentUpdate',
  label: 'Aggiornamento più recente',
  value: {
    updatedAt: SortOrder.DESC,
  },
};

export const LEAST_RECENT_UPDATE_SORT_OPTION: JobApplicationSerpSortOption = {
  key: 'leastRecentUpdate',
  label: 'Aggiornamento meno recente',
  value: {
    updatedAt: SortOrder.ASC,
  },
};

export const MOST_RECENT_INTERVIEW_SORT_OPTION: JobApplicationSerpSortOption = {
  key: 'mostRecentInterview',
  label: 'Colloquio più recente',
  value: {
    'interview.date': SortOrder.DESC,
  },
};

export const LEAST_RECENT_INTERVIEW_SORT_OPTION: JobApplicationSerpSortOption = {
  key: 'leastRecentInterview',
  label: 'Colloquio meno recente',
  value: {
    'interview.date': SortOrder.ASC,
  },
};

export const SORT_OPTIONS: JobApplicationSerpSortOption[] = [
  DEFAULT_SORT_OPTION,
  MOST_RECENT_CREATION_SORT_OPTION,
  LEAST_RECENT_CREATION_SORT_OPTION,
  MOST_RECENT_UPDATE_SORT_OPTION,
  LEAST_RECENT_UPDATE_SORT_OPTION,
  MOST_RECENT_INTERVIEW_SORT_OPTION,
  LEAST_RECENT_INTERVIEW_SORT_OPTION,
];

export const SORT_OPTIONS_PER_FILTER: Record<JobApplicationSerpPageFilterKey, JobApplicationSerpSortOption> = {
  toBeManaged: {
    ...DEFAULT_SORT_OPTION,
    key: 'default',
  },
  inManagement: {
    ...DEFAULT_SORT_OPTION,
    key: 'default',
  },
  assigned: {
    ...DEFAULT_SORT_OPTION,
    key: 'default',
  },
  inProposal: {
    ...DEFAULT_SORT_OPTION,
    key: 'default',
  },
  archived: {
    ...DEFAULT_SORT_OPTION,
    key: 'default',
  },
  trashed: {
    ...DEFAULT_SORT_OPTION,
    key: 'default',
  },
  pastInterviews: {
    ...MOST_RECENT_INTERVIEW_SORT_OPTION,
  },
  futureInterviews: {
    ...LEAST_RECENT_INTERVIEW_SORT_OPTION,
  },
  all: {
    ...DEFAULT_SORT_OPTION,
    key: 'default',
  },
};

export const FILTERS_MAPPER = (ctx: JobApplicationSerpPageFilterOptionCtx): FiltersMapper<JobApplicationSerpPageFilters> => ({
  q: (v) => v,
  filter: (v) => FILTER_OPTIONS(ctx).find(({ key }) => key === v)?.key || DEFAULT_FILTER_OPTION(ctx),
  sort: (v) => SORT_KEYS.find((key) => key === v) || DEFAULT_SORT_OPTION.key,
});

export const getCombinedFiltersMapper = (
  ctx: JobApplicationSerpPageFilterOptionCtx,
): FiltersMapper<CombinedJobApplicationFilters> => ({
  ...FILTERS_MAPPER(ctx),

  ...serpJobApplicationAdvancedFiltersMapper,
});
